// @flow
import style from 'assets/style.json';
import { FEATURE_STYLE_MAPPING, isFeatureDisabled } from './auth';

const MVT_MAX_ZOOM = 14;
const TILE_ORIGIN = window.location.origin;

export const STYLE_CATEGORIES = {
  temporary: {
    color: '#FB8200',
    label: 'Eseti légterek',
    showInLayerDrawer: true,
    enabledByDefault: true,
  },

  inventory: {
    color: '#FFC326',
    label: 'Beadott igénylések',
    showInLayerDrawer: true,
    style: { dashed: true },
    enabledByDefault: true,
  },

  restricted: {
    color: '#FF4081',
    label: 'Korlátozott légterek',
    showInLayerDrawer: true,
    style: { hatched: true },
    enabledByDefault: true,
  },

  tra: {
    color: '#FF4081',
    label: 'Időszakosan korlátozott légterek',
    showInLayerDrawer: true,
    style: { hatched: true },
    enabledByDefault: true,
  },

  prohibited: {
    color: '#FF4081',
    label: 'Tiltott légterek',
    showInLayerDrawer: true,
    style: { hatched: true },
    enabledByDefault: true,
  },

  /*
  'dji-flyzone': {
    color: '#FF4081',
    label: 'DJI Geo Zones',
    showInLayerDrawer: true,
    style: { dashed: true },
    enabledByDefault: false, // FlyZones are disabled by default
    featureFlag: 'hasSubscription',
  },
  */

  nodronezone: {
    color: '#FF4081',
    label: 'No drone zone-ok',
    showInLayerDrawer: true,
    enabledByDefault: true,
    style: { dashed: true },
  },

  controlled: {
    color: '#2962FF',
    label: 'Ellenőrzött légterek',
    showInLayerDrawer: true,
    enabledByDefault: true,
  },

  tiz: {
    color: '#2962FF',
    label: 'Forgalmi tájékoztató körzetek',
    showInLayerDrawer: true,
    enabledByDefault: true,
  },

  misc: {
    color: '#3A5C6B',
    label: 'Egyéb légterek',
    showInLayerDrawer: true,
    enabledByDefault: true,
  },

  search: {
    color: '#3BB2D0',
    showInLayerDrawer: false,
    enabledByDefault: true,
  },

  default: {
    color: '#3A5C6B',
    showInLayerDrawer: false,
    enabledByDefault: true,
  },

  'tra-temp': {
    color: '#FF4081',
    showInLayerDrawer: false,
    enabledByDefault: true,
  },

  'tra-inv': {
    color: '#FF4081',
    showInLayerDrawer: false,
    enabledByDefault: true,
  },

  env: {
    color: '#4CAF50',
    showInLayerDrawer: false,
    enabledByDefault: true,
  },

  airport: {
    color: '#B0BEC5',
    showInLayerDrawer: false,
    enabledByDefault: true,
  },
};

export function getFeatureColor(feature) {
  if (feature.type === 'ac') {
    return '#000000';
  }

  if (feature.type === 'ap' || !(feature.style_category in STYLE_CATEGORIES)) {
    return STYLE_CATEGORIES.default.color;
  }
  return STYLE_CATEGORIES[feature.style_category].color;
}

export default function getMapStyle({
  visibleAirspaces,
  selectedFeature,

  background,
  styleCategories,

  showAirports,
  showExtrusion,
}) {
  // FIXME: This hack is to ensure that the style categories `tra-temp` and
  // `tra-inv` get toggled with the style category `restricted`.

  const enabledStyleCategories = styleCategories.filter((styleCategorie) => {
    const feature = FEATURE_STYLE_MAPPING[styleCategorie];
    return !isFeatureDisabled(feature);
  });

  const layers = style.layers.map(l => {
    if (l.source === 'vt' && l['source-layer'] !== 'airports') {
      // Filter airspaces
      return {
        ...l,

        filter: [
          'all',
          ...(l.filter ? [l.filter] : []),
          ['in', '$id', ...visibleAirspaces],
          ['in', 'switcher_category', ...enabledStyleCategories],
        ],

        layout: {
          ...l.layout,
          visibility:
            showExtrusion === l.id.startsWith('airspaces-extrusion') ||
            l.id.includes('label')
              ? 'visible'
              : 'none',
        },
      };
    } else if (l.source === 'vt' && l['source-layer'] === 'airports') {
      // Always show airports
      const visible = !isFeatureDisabled('airports');
      return {
        ...l,
        layout: {
          ...l.layout,
          visibility: showAirports && visible ? 'visible' : 'none',
        },
      };
    } else if (l.source === 'selection') {
      return {
        ...l,
        filter: [
          '==',
          'slug',
          selectedFeature &&
          selectedFeature.type ===
            (l['source-layer'] === 'airspaces' ? 'as' : 'ap')
            ? selectedFeature.slug
            : 0,
        ],
      };
    } else if (!l.id.match(/admin-.*-boundaries/)) {
      // Hide background layers when satellite imagery is shown, except for administrative boundaries
      return {
        ...l,
        layout: {
          ...l.layout,
          visibility: background === 'osm' ? 'visible' : 'none',
        },
      };
    }
    return l;
  });

  return {
    ...style,

    layers: [
      {
        id: 'mapbox_satellite',
        type: 'raster',
        source: 'mapbox_satellite',
        'source-layer': 'mapbox_satellite_full',
        minzoom: 0,
        maxzoom: 22,
        layout: {
          visibility: background === 'satellite' ? 'visible' : 'none',
        },
      },
      ...layers,
    ],

    sources: {
      composite: {
        url: 'mapbox://mapbox.mapbox-terrain-v2,mapbox.mapbox-streets-v8',
        type: 'vector',
      },
      mapbox_satellite: {
        type: 'raster',
        url: 'mapbox://mapbox.satellite',
        tileSize: 256,
      },
      vt: {
        type: 'vector',
        tiles: [`${TILE_ORIGIN}/vt/{z}/{x}/{y}.pbf`],
        maxzoom: MVT_MAX_ZOOM,
      },
      selection: {
        type: 'vector',
        tiles: [`${TILE_ORIGIN}/vt/{z}/{x}/{y}.pbf`],
        maxzoom: MVT_MAX_ZOOM,
      },
      search: {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features:
            selectedFeature && selectedFeature.type === 's'
              ? [{ type: 'Feature', geometry: selectedFeature.boundary }]
              : [],
        },
      },
    },
    glyphs: 'mapbox://fonts/mapbox/{fontstack}/{range}.pbf',
    sprite: `${TILE_ORIGIN}/assets/sprites/sprites`,
  };
}



// WEBPACK FOOTER //
// ./utils/mapStyle.js